import {
  bandToDiscountPct,
  discountPctToBand,
} from "../../../Pages/PromoSimulator/utils/promoDiscountBands";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const WEEKS = Array.from({ length: 52 }, (_, i) => i + 1);

const SKUS = [
  { id: "sku_1_1", label: "Alpha 200g", brand: "BrandAlpha", manufacturer: "Savola" },
  { id: "sku_1_2", label: "Alpha 500g", brand: "BrandAlpha", manufacturer: "Savola" },
  { id: "sku_2_1", label: "Beta Classic", brand: "BrandBeta", manufacturer: "Savola" },
  { id: "sku_3_2", label: "Gamma Max", brand: "BrandGamma", manufacturer: "Savola" },
];

function hashString(input = "") {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function makeCell({ skuId, week, discount, scenarioBump = 0 }) {
  const seed = (week * 13 + skuId.length * 7 + scenarioBump) % 17;
  const baseSales = Math.round(9000 + seed * 800 + scenarioBump * 30);
  const upliftPct = Math.max(
    0,
    Math.round((discount * 0.85 + seed * 0.35 + scenarioBump * 0.15) * 10) / 10
  );
  const incrementalSales = Math.round((baseSales * upliftPct) / 100);
  const roi = Math.round((1.1 + seed / 50 - discount / 120 + scenarioBump * 0.01) * 100) / 100;
  const marginImpact = Math.round((-discount * 0.22 + seed * 0.08) * 10) / 10;
  const cannibalisation = Math.round((discount > 20 ? discount * 0.24 : discount * 0.11) * 10) / 10;

  return {
    skuId,
    week,
    discountPct: discount,
    kpis: {
      baseSales,
      incrementalSales,
      upliftPct,
      roi,
      marginImpact,
      cannibalisation,
    },
  };
}

function buildScenario({ id, name, discountsBySku = {}, scenarioBump = 0 }) {
  const rows = SKUS.map((sku) => {
    const skuDiscount = discountsBySku[sku.id] ?? 10;
    const cells = {};
    for (const week of WEEKS) {
      const discount = week % 8 === 0 ? skuDiscount + 5 : skuDiscount;
      cells[week] = makeCell({ skuId: sku.id, week, discount, scenarioBump });
    }
    return {
      skuId: sku.id,
      skuLabel: sku.label,
      brand: sku.brand,
      manufacturer: sku.manufacturer,
      cells,
    };
  });

  return {
    id,
    name,
    weeks: WEEKS,
    rows,
    modifiedCells: {},
  };
}

const RECOMMENDED_SCENARIO = buildScenario({
  id: "recommended",
  name: "Recommended Baseline",
  discountsBySku: {
    sku_1_1: 10,
    sku_1_2: 12,
    sku_2_1: 15,
    sku_3_2: 8,
  },
});

const SAVED_SCENARIOS = [
  RECOMMENDED_SCENARIO,
  buildScenario({
    id: "scenario_early_lift",
    name: "Early Lift Plan",
    discountsBySku: { sku_1_1: 15, sku_1_2: 10, sku_2_1: 18, sku_3_2: 10 },
  }),
  buildScenario({
    id: "scenario_margin_safe",
    name: "Margin Safe Plan",
    discountsBySku: { sku_1_1: 8, sku_1_2: 8, sku_2_1: 10, sku_3_2: 6 },
  }),
];

function buildScenarioFromSaved(savedScenario = {}) {
  const scenarioId = savedScenario.id || `saved_${Date.now()}`;
  const scenarioName = savedScenario.name || "Saved Scenario";
  const baseDiscount = Number(savedScenario.discountPct);
  const safeBaseDiscount = Number.isFinite(baseDiscount) ? baseDiscount : 12;
  const scenarioBump = hashString(String(scenarioId)) % 7;

  const discountsBySku = {
    sku_1_1: Math.max(0, Math.min(60, safeBaseDiscount)),
    sku_1_2: Math.max(0, Math.min(60, safeBaseDiscount + 2)),
    sku_2_1: Math.max(0, Math.min(60, safeBaseDiscount - 1)),
    sku_3_2: Math.max(0, Math.min(60, safeBaseDiscount + 1)),
  };

  return buildScenario({
    id: scenarioId,
    name: scenarioName,
    discountsBySku,
    scenarioBump,
  });
}

export const mockPromoService = {
  async getRecommendedPlan() {
    await delay(450);
    return RECOMMENDED_SCENARIO;
  },

  async getSavedScenarios() {
    await delay(350);
    return SAVED_SCENARIOS.map(({ id, name }) => ({ id, name }));
  },

  async getScenarioById(id) {
    await delay(250);
    const scenario = SAVED_SCENARIOS.find((s) => s.id === id);
    if (!scenario) {
      return RECOMMENDED_SCENARIO;
    }
    return scenario;
  },

  async getPlanForSavedScenario(savedScenario) {
    await delay(350);
    return buildScenarioFromSaved(savedScenario);
  },

  async simulatePromo({ skuId, week, discountPct, overrides = {} }) {
    await delay(500);
    const scenarioBump = hashString(`${skuId}_${week}`) % 5;

    const nextBand =
      overrides.selectedBand ??
      overrides.kpis?.selectedBand ??
      (overrides.discountPct !== undefined
        ? discountPctToBand(overrides.discountPct)
        : null);
    const nextDiscount =
      overrides.discountPct !== undefined
        ? Number(overrides.discountPct)
        : nextBand
          ? bandToDiscountPct(nextBand)
          : Number(discountPct);
    const safeDiscount = Number.isFinite(nextDiscount) ? nextDiscount : 10;
    const safeBand = nextBand ?? discountPctToBand(safeDiscount);

    let cell = makeCell({
      skuId,
      week,
      discount: safeDiscount,
      scenarioBump,
    });
    cell.selectedBand = safeBand;
    cell.kpis = { ...cell.kpis, selectedBand: safeBand };

    const o = overrides.kpis || {};
    const k = { ...cell.kpis };

    if (o.baseSales !== undefined && Number.isFinite(Number(o.baseSales))) {
      k.baseSales = Math.round(Number(o.baseSales));
    }
    if (
      o.upliftPct !== undefined &&
      Number.isFinite(Number(o.upliftPct))
    ) {
      k.upliftPct = Math.round(Number(o.upliftPct) * 10) / 10;
    }
    if (
      o.roi !== undefined &&
      Number.isFinite(Number(o.roi))
    ) {
      k.roi = Math.round(Number(o.roi) * 100) / 100;
    }
    if (
      o.marginImpact !== undefined &&
      Number.isFinite(Number(o.marginImpact))
    ) {
      k.marginImpact = Math.round(Number(o.marginImpact) * 10) / 10;
    }
    if (
      o.cannibalisation !== undefined &&
      Number.isFinite(Number(o.cannibalisation))
    ) {
      k.cannibalisation = Math.round(Number(o.cannibalisation) * 10) / 10;
    }

    k.incrementalSales = Math.round((k.baseSales * k.upliftPct) / 100);

    cell = {
      ...cell,
      discountPct: safeDiscount,
      kpis: k,
    };

    return { cell, affected: [] };
  },
};

